import React from 'react';
import './VisitorMapFooter.css';

export interface VisitorCountryStat {
  country_code: string;
  country_name: string;
  visit_count: number;
  latitude: number | null;
  longitude: number | null;
}

export interface VisitorStats {
  total_visits: number;
  counted: boolean;
  mapped_visits: number;
  countries: VisitorCountryStat[];
}

interface VisitorMapFooterProps {
  stats: VisitorStats | null;
}

const MAP_WIDTH = 900;
const MAP_HEIGHT = 440;

const projectPoint = (latitude: number, longitude: number) => ({
  x: ((longitude + 180) / 360) * MAP_WIDTH,
  y: ((90 - latitude) / 180) * MAP_HEIGHT,
});

const countryFlag = (countryCode: string) => {
  if (!/^[A-Z]{2}$/.test(countryCode)) return '•';
  return String.fromCodePoint(...countryCode.split('').map((letter) => 127397 + letter.charCodeAt(0)));
};

const WorldMap = ({ countries }: { countries: VisitorCountryStat[] }) => {
  const locatedCountries = countries.filter(
    (country): country is VisitorCountryStat & { latitude: number; longitude: number } =>
      typeof country.latitude === 'number' && typeof country.longitude === 'number'
  );

  return (
    <div className="visitor-map__canvas">
      <svg
        className="visitor-map__svg"
        viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
        role="img"
        aria-labelledby="visitor-map-title visitor-map-description"
      >
        <title id="visitor-map-title">STINGExoDB visitor map</title>
        <desc id="visitor-map-description">
          Anonymous country-level distribution of visitors to STINGExoDB.
        </desc>

        <g className="visitor-map__grid" aria-hidden="true">
          <path d="M0 110H900 M0 220H900 M0 330H900" />
          <path d="M150 0V440 M300 0V440 M450 0V440 M600 0V440 M750 0V440" />
        </g>

        <g className="visitor-map__land" aria-hidden="true">
          <path d="M56 91C88 52 147 38 201 49l30 21 48-5 45 25 2 31-35 22-28 4-18 28-26 4-25 39-33 4-15-27-34-17-12-36-34-18-10-33Z" />
          <path d="M255 54l27-29 48-10 28 15-14 34-37 14-24-3-28-21Z" />
          <path transform="translate(55 0)" d="M219 230l35 6 31 28 19 51-16 45-18 56-24-29-10-55-25-42-13-36 21-24Z" />
          <path d="M415 89l36-22 52 4 24 19 59-4 45-31 76 5 61 28 65 3 27 27-20 33-57 1-26 22-51-6-38 25-55-7-42-33-29 10-31-20-44 7-28-21-4-40Z" />
          <path d="M456 181l50-21 43 21 25 53-14 42-32 60-35 5-21-46-31-43 2-46 13-25Z" />
          <path d="M659 204l19-9 18 18-10 34-22-11-5-32Z" />
          <path d="M724 301l47-21 49 16 15 31-37 27-55-7-19-46Z" />
          <path d="M845 363l19-13 13 20-19 26-13-33Z" />
          <path d="M41 422c126-18 257-22 392-13 149-11 295-5 428 13-144 16-286 20-425 10-142 11-274 7-395-10Z" />
        </g>

        <g className="visitor-map__markers">
          {locatedCountries.map((country, index) => {
            const point = projectPoint(country.latitude, country.longitude);
            const radius = Math.min(15, 5 + Math.log2(country.visit_count + 1) * 1.8);
            return (
              <g key={country.country_code} transform={`translate(${point.x} ${point.y})`}>
                <g
                  className="visitor-map__marker"
                  style={{ animationDelay: `${index * 120}ms` }}
                >
                  <title>{`${country.country_name}: ${country.visit_count.toLocaleString()} mapped visit${country.visit_count === 1 ? '' : 's'}`}</title>
                  <circle className="visitor-map__marker-halo" r={radius + 7} />
                  <circle className="visitor-map__marker-core" r={radius} />
                  <circle className="visitor-map__marker-glint" cx={-radius * 0.25} cy={-radius * 0.25} r={Math.max(1.5, radius * 0.2)} />
                </g>
              </g>
            );
          })}
        </g>
      </svg>

      {locatedCountries.length === 0 && (
        <div className="visitor-map__empty" role="status">
          <span className="visitor-map__empty-dot" aria-hidden="true" />
          Mapping new visits now
        </div>
      )}
    </div>
  );
};

const VisitorMapFooter = ({ stats }: VisitorMapFooterProps) => {
  const countries = stats?.countries ?? [];
  const topCountries = countries.slice(0, 4);
  const trackedVisits = stats?.total_visits;
  const mappedVisits = stats?.mapped_visits ?? 0;

  return (
    <section className="visitor-map" aria-labelledby="visitor-map-heading">
      <div className="visitor-map__story">
        <div className="visitor-map__eyebrow">
          <span className="visitor-map__live-dot" aria-hidden="true" />
          Global research community
        </div>
        <h2 id="visitor-map-heading">STINGExoDB around the world</h2>

        <div className="visitor-map__metrics" aria-label="Visitor statistics">
          <div className="visitor-map__metric visitor-map__metric--primary">
            <span>{typeof trackedVisits === 'number' ? trackedVisits.toLocaleString() : '—'}</span>
            <small>Tracked visits since August 2026</small>
          </div>
          <div className="visitor-map__metric">
            <span>{countries.length.toLocaleString()}</span>
            <small>{countries.length === 1 ? 'Country represented' : 'Countries represented'}</small>
          </div>
          <div className="visitor-map__metric">
            <span>{mappedVisits.toLocaleString()}</span>
            <small>Mapped visits</small>
          </div>
        </div>

        {topCountries.length > 0 && (
          <div className="visitor-map__country-list" aria-label="Top visitor countries">
            {topCountries.map((country) => (
              <div className="visitor-map__country" key={country.country_code}>
                <span className="visitor-map__flag" aria-hidden="true">{countryFlag(country.country_code)}</span>
                <span className="visitor-map__country-name">{country.country_name}</span>
                <strong>{country.visit_count.toLocaleString()}</strong>
              </div>
            ))}
          </div>
        )}

        <p className="visitor-map__privacy">
          Country-level totals are anonymized. Geographic tracking began in August 2026; no raw IP addresses are stored.
        </p>
      </div>

      <div className="visitor-map__visual">
        <div className="visitor-map__visual-heading">
          <div>
            <span>Live visitor map</span>
            <small>Each point represents a country accessing STINGExoDB</small>
          </div>
          <span className="visitor-map__coordinate">GLOBAL / 90°N—90°S</span>
        </div>
        <WorldMap countries={countries} />
        <p className="visitor-map__attribution">
          GeoLite2 country data by{' '}
          <a href="https://www.maxmind.com" target="_blank" rel="noopener noreferrer">MaxMind</a>.
        </p>
      </div>
    </section>
  );
};

export default VisitorMapFooter;
